import {
  BadgeCheck,
  Bike,
  CreditCard,
  Eye,
  FileBadge,
  Hash,
  Layers,
  Mail,
  MapPin,
  Pen,
  Phone,
} from "lucide-react";

// =====================================================
// KYC BADGE
// =====================================================

const KycBadge = ({ children, color = "gray" }) => {
  const styles = {
    gray: "bg-gray-100 text-gray-600",
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    orange: "bg-orange-100 text-orange-700",
    purple: "bg-purple-100 text-purple-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        styles[color]
      }`}
    >
      {children}
    </span>
  );
};

// =====================================================
// KYC STATUS COLOR
// =====================================================

const getKycStatusColor = (status) => {
  if (status === "Approved") {
    return "green";
  }

  if (status === "Rejected") {
    return "red";
  }

  return "yellow";
};

// =====================================================
// KYC TABLE
// =====================================================

const DriverKycTable = ({
  currentKycDrivers = [],

  setSelectedDriver,
  setOpenKycDialogId,

  setEditKycDriver,
  populateEditForm,
  setEditKycModalOpen,
}) => {
  return (
    <div className="w-full min-w-0 overflow-hidden rounded-2xl border shadow-sm">
      {/* =================================================
          MOBILE CARD LIST
      ================================================= */}

      <div
        className="md:hidden w-full min-w-0 overflow-y-auto divide-y divide-gray-100 bg-white"
        style={{
          height: "calc(100vh - 300px)",
        }}
      >
        {currentKycDrivers?.map((driver) => (
          <div key={driver.driver_code || driver.id} className="p-4">
            {/* =========================================
                  HEADER
              ========================================= */}

            <div className="flex items-center gap-3 min-w-0">
              <img
                src={driver.profile_photo || "/placeholder-driver.png"}
                alt={driver.name || "Driver"}
                onError={(e) => {
                  e.currentTarget.src =
                    "https://ui-avatars.com/api/?name=" +
                    encodeURIComponent(driver.name || "Driver") +
                    "&background=F3F4F6&color=374151";
                }}
                className="h-12 w-12 rounded-xl border border-gray-200 object-cover shrink-0"
              />

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {driver.name || "N/A"}
                  </p>

                  <KycBadge color="blue">
                    {driver.driver_code || "N/A"}
                  </KycBadge>
                </div>

                <div className="mt-1 space-y-1 text-xs text-gray-600">
                  <div className="flex items-center gap-2 min-w-0">
                    <Phone size={13} className="shrink-0 text-gray-400" />

                    <span className="truncate">{driver.phone || "N/A"}</span>
                  </div>

                  <div className="flex items-center gap-2 min-w-0">
                    <Mail size={13} className="shrink-0 text-gray-400" />

                    <span className="truncate">{driver.email || "N/A"}</span>
                  </div>
                </div>
              </div>

              <KycBadge color={getKycStatusColor(driver.status)}>
                {driver.status || "Pending"}
              </KycBadge>
            </div>

            {/* =========================================
                  KYC + VEHICLE
              ========================================= */}

            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-gray-700">
              {/* AADHAAR */}

              <div className="flex items-center gap-2 min-w-0">
                <CreditCard size={13} className="shrink-0 text-gray-400" />

                <span className="font-medium text-gray-500">Aadhaar:</span>

                <span className="truncate">{driver.aadhaar_no || "N/A"}</span>
              </div>

              {/* PAN */}

              <div className="flex items-center gap-2 min-w-0">
                <FileBadge size={13} className="shrink-0 text-gray-400" />

                <span className="font-medium text-gray-500">PAN:</span>

                <span className="truncate">{driver.pan_no || "N/A"}</span>
              </div>

              {/* DL */}

              <div className="flex items-center gap-2 min-w-0">
                <BadgeCheck size={13} className="shrink-0 text-gray-400" />

                <span className="font-medium text-gray-500">DL:</span>

                <span className="truncate">
                  {driver.driving_licence || "N/A"}
                </span>
              </div>

              {/* VEHICLE */}

              <div className="flex items-center gap-2 min-w-0">
                <Bike size={13} className="shrink-0 text-gray-400" />

                <span className="truncate">
                  {driver.transport_type_id || "N/A"}
                </span>
              </div>

              {/* NUMBER */}

              <div className="flex items-center gap-2 min-w-0 col-span-2">
                <Hash size={13} className="shrink-0 text-gray-400" />

                <span className="truncate">
                  {driver.licence_plate || "N/A"}
                </span>
              </div>
            </div>

            {/* =========================================
                  LOCATION
              ========================================= */}

            <div className="mt-2 space-y-1 text-xs text-gray-700">
              <div className="flex min-w-0 items-start gap-2">
                <MapPin size={13} className="mt-0.5 shrink-0 text-gray-400" />

                <span className="min-w-0 break-words leading-4">
                  {driver.city || "N/A"}

                  {driver.area ? ` • ${driver.area}` : ""}
                </span>
              </div>

              {!!driver.current_step && (
                <div className="flex items-center gap-2 min-w-0">
                  <Layers size={13} className="shrink-0 text-gray-400" />

                  <span className="truncate">Step {driver.current_step}</span>
                </div>
              )}
            </div>

            {/* =========================================
                  ACTIONS
              ========================================= */}

            <div className="mt-3 flex items-center justify-end gap-2">
              {/* VIEW */}

              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-800"
                title="Show Details"
                onClick={() => {
                  setSelectedDriver(driver);

                  setOpenKycDialogId(driver.id || driver.driver_code);
                }}
              >
                <Eye size={16} />
              </button>

              {/* EDIT */}

              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-600 transition hover:bg-blue-50 hover:text-blue-700"
                title="Edit KYC"
                onClick={() => {
                  setEditKycDriver(driver);

                  populateEditForm(driver);

                  setEditKycModalOpen(true);
                }}
              >
                <Pen size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* =================================================
          DESKTOP TABLE
      ================================================= */}

      <div
        className="hidden md:block w-full min-w-0 overflow-auto"
        style={{
          height: "calc(100vh - 300px)",
        }}
      >
        <table className="w-full table-fixed">
          {/* =============================================
              HEADER
          ============================================= */}

          <thead className="border-b sticky top-0 z-20 border-gray-200 bg-[#2EC8F1] text-white">
            <tr>
              <th className="w-[21%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                Driver
              </th>

              <th className="w-[20%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                KYC Details
              </th>

              <th className="w-[16%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                Vehicle
              </th>

              <th className="w-[35%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
                Location / Status
              </th>

              <th className="w-[8%] px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>

          {/* =============================================
              BODY
          ============================================= */}

          <tbody className="divide-y divide-gray-100 bg-white">
            {currentKycDrivers?.map((driver) => (
              <tr
                key={driver.driver_code || driver.id}
                className="text-sm transition hover:bg-gray-50"
              >
                {/* =====================================
                      DRIVER
                  ===================================== */}

                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={driver.profile_photo || "/placeholder-driver.png"}
                      alt={driver.name || "Driver"}
                      onError={(e) => {
                        e.currentTarget.src =
                          "https://ui-avatars.com/api/?name=" +
                          encodeURIComponent(driver.name || "Driver") +
                          "&background=F3F4F6&color=374151";
                      }}
                      className="h-11 w-11 rounded-xl border border-gray-200 object-cover shrink-0"
                    />

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {driver.name || "N/A"}
                        </p>

                        <KycBadge color="blue">
                          {driver.driver_code || "N/A"}
                        </KycBadge>
                      </div>

                      <div className="mt-1 space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-2 min-w-0">
                          <Phone size={13} className="shrink-0 text-gray-400" />

                          <span className="truncate">
                            {driver.phone || "N/A"}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 min-w-0">
                          <Mail size={13} className="shrink-0 text-gray-400" />

                          <span className="truncate">
                            {driver.email || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>

                {/* =====================================
                      KYC DETAILS
                  ===================================== */}

                <td className="px-4 py-3">
                  <div className="space-y-1 text-xs text-gray-700">
                    <div className="flex items-center gap-2 min-w-0">
                      <CreditCard
                        size={13}
                        className="shrink-0 text-gray-400"
                      />

                      <span className="font-medium text-gray-500">
                        Aadhaar:
                      </span>

                      <span className="truncate">
                        {driver.aadhaar_no || "N/A"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <FileBadge size={13} className="shrink-0 text-gray-400" />

                      <span className="font-medium text-gray-500">PAN:</span>

                      <span className="truncate">{driver.pan_no || "N/A"}</span>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <BadgeCheck
                        size={13}
                        className="shrink-0 text-gray-400"
                      />

                      <span className="font-medium text-gray-500">DL:</span>

                      <span className="truncate">
                        {driver.driving_licence || "N/A"}
                      </span>
                    </div>
                  </div>
                </td>

                {/* =====================================
                      VEHICLE
                  ===================================== */}

                <td className="px-4 py-3">
                  <div className="space-y-1 text-xs text-gray-700">
                    <div className="flex items-center gap-2 min-w-0">
                      <Bike size={13} className="shrink-0 text-gray-400" />

                      <span className="truncate">
                        {driver.transport_type_id || "N/A"}

                        {driver.transport_description
                          ? ` • ${driver.transport_description}`
                          : ""}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <Hash size={13} className="shrink-0 text-gray-400" />

                      <span className="truncate">
                        {driver.licence_plate || "N/A"}
                      </span>
                    </div>
                  </div>
                </td>

                {/* =====================================
                      LOCATION / STATUS
                  ===================================== */}

                <td className="px-4 py-3">
                  <div className="space-y-2">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex min-w-0 items-start gap-2">
                        <MapPin
                          size={13}
                          className="mt-0.5 shrink-0 text-gray-400"
                        />

                        <span
                          className="line-clamp-2 min-w-0 break-words leading-4"
                          title={`${driver.city || "N/A"}${
                            driver.area ? ` • ${driver.area}` : ""
                          }`}
                        >
                          {driver.city || "N/A"}

                          {driver.area ? ` • ${driver.area}` : ""}
                        </span>
                      </div>

                      {!!driver.current_step && (
                        <div className="flex items-center gap-2 min-w-0">
                          <Layers
                            size={13}
                            className="shrink-0 text-gray-400"
                          />

                          <span className="truncate">
                            Step {driver.current_step}
                          </span>
                        </div>
                      )}
                    </div>

                    <KycBadge color={getKycStatusColor(driver.status)}>
                      {driver.status || "Pending"}
                    </KycBadge>
                  </div>
                </td>

                {/* =====================================
                      ACTIONS
                  ===================================== */}

                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-800"
                      title="Show Details"
                      onClick={() => {
                        setSelectedDriver(driver);

                        setOpenKycDialogId(driver.id || driver.driver_code);
                      }}
                    >
                      <Eye size={15} />
                    </button>

                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-600 transition hover:bg-blue-50 hover:text-blue-700"
                      title="Edit KYC"
                      onClick={() => {
                        setEditKycDriver(driver);

                        populateEditForm(driver);

                        setEditKycModalOpen(true);
                      }}
                    >
                      <Pen size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DriverKycTable;
